import { Badge, Button, Card, Group, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { PageHeader } from '../../ui/PageHeader';
import { QueryState } from '../../ui/QueryState';

interface Incident {
  id: number;
  categoryLabel: string;
  description: string;
  occurredAt: string | null;
  urgent: boolean;
  createdAt: string;
  acceptedAt: string | null;
  acceptedByName: string | null;
  reporter: string | null;
  reporterEmail: string | null;
  department: string | null;
}

export function IncidentsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['incidents'], queryFn: () => api.get<Incident[]>('/admin/incidents') });
  const accept = useMutation({
    mutationFn: (id: number) => api.post(`/admin/incidents/${id}/accept`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['incidents'] }),
    onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
  });
  return (
    <>
      <PageHeader title="Инциденты" subtitle="Сообщения сотрудников через кнопку «Сообщить об инциденте». Каждое также уходит письмом в Security." />
      <QueryState loading={q.isLoading} error={q.error}>
        {() => (
          <Card withBorder radius="lg" p={0}>
            <Table.ScrollContainer minWidth={900}>
              <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
                <Table.Thead><Table.Tr><Table.Th>#</Table.Th><Table.Th>Получено</Table.Th><Table.Th>Сотрудник</Table.Th><Table.Th>Что случилось</Table.Th><Table.Th>Статус</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {q.data!.map((i) => (
                    <Table.Tr key={i.id} style={{ verticalAlign: 'top' }}>
                      <Table.Td><Text size="sm" fw={600}>{i.id}</Text></Table.Td>
                      <Table.Td><Text size="xs" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(i.createdAt)}</Text>{i.occurredAt && <Text size="xs" c="dimmed">когда: {i.occurredAt}</Text>}</Table.Td>
                      <Table.Td><Text size="sm" fw={500}>{i.reporter ?? '-'}</Text><Text size="xs" c="dimmed">{i.department ?? ''} {i.reporterEmail ?? ''}</Text></Table.Td>
                      <Table.Td maw={420}>
                        <Stack gap={4}>
                          <Group gap={6}>{i.urgent && <Badge color="red">срочно</Badge>}<Text size="sm" fw={500}>{i.categoryLabel}</Text></Group>
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{i.description}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        {i.acceptedAt
                          ? <Stack gap={0}><Badge color="teal" variant="light">принято</Badge><Text size="xs" c="dimmed">{i.acceptedByName}, {fmtDateTime(i.acceptedAt)}</Text></Stack>
                          : <Button size="xs" variant="light" leftSection={<IconCheck size={14} />} loading={accept.isPending && accept.variables === i.id} onClick={() => accept.mutate(i.id)}>Принято</Button>}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {q.data!.length === 0 && <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" size="sm" ta="center" py="md">Сообщений пока нет</Text></Table.Td></Table.Tr>}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        )}
      </QueryState>
    </>
  );
}
